import { SMS_METRICS } from 'modules/billing/constants';
import { OrgBillingPartyRecipientCountryMetric } from 'modules/domains/billing';
import { sortObjectListByKey, sumObjectListByKey } from 'utils/arrayHelpers';
import {
  MMS_ATTACHMENTS_RECEIVED,
  MMS_ATTACHMENTS_SENT,
  MMS_ATTACHMENTS_TOTAL,
  SMS_SEGMENTS_RECEIVED,
  SMS_SEGMENTS_SENT,
  SMS_SEGMENTS_TOTAL,
  SUCCESSFUL_SMS_VERIFICATIONS,
} from '../constants';
import { Metric, OrganizationGlobalSmsMetric, Project, ProjectMetric } from '../types';
import { isProjectGlobalSmsMetric } from './matchers';
import { TableMetric } from '../components/Index/AnalyticTable';

/**
 * Calculate Global SMS sends by country
 * Each country can have either 'FirstParty' or 'ThirdParty' sends, so we want to aggregate those
 * values into a single value
 *
 * Sort each country descending by total number of sends, and if the list is longer than a max value
 * provided, we aggregate the remaining countries into a single table row with a total sum value
 */
export const createGlobalSmsRows = (
  metrics: OrgBillingPartyRecipientCountryMetric[],
): TableMetric[] => {
  /** Group metrics by country  */
  const smsSentByCountry = metrics.reduce(
    (acc, curr) => {
      // filter out other metrics
      if (curr.metricName !== SMS_METRICS.smsSegmentsSent) {
        return acc;
      }

      const country = curr.recipientCountryName;
      const countryExists = !!acc[country];
      const value = countryExists ? acc[country].value + curr.value : curr.value;

      return { ...acc, [country]: { recipientCountryName: curr.recipientCountryName, value } };
    },
    {} as Record<string, { recipientCountryName: string; value: number }>,
  );

  /** Sort countries based on send value, and group top sending countries */
  const sortedSmsMetrics = sortObjectListByKey(Object.values(smsSentByCountry), 'value', true);
  const maxRows = 5;
  const topSmsMetrics = sortedSmsMetrics.slice(0, maxRows);
  const additionalSmsMetrics = sortedSmsMetrics.slice(maxRows);
  const remainingMetricsSum = sumObjectListByKey(additionalSmsMetrics, 'value');

  /** Create table rows, and add a row of "all other countries" sum if list is larger than maxRows */
  const tableMetrics: TableMetric[] = topSmsMetrics.map((metric) => ({
    metricName: { label: metric.recipientCountryName },
    values: [{ value: metric.value }],
  }));
  const sumMetric = {
    metricName: { label: 'All other countries' },
    values: [{ isTotal: true, value: remainingMetricsSum as number }],
  };

  return sortedSmsMetrics.length > maxRows ? [...tableMetrics, sumMetric] : tableMetrics;
};

type GlobalSmsDisplayMetric = {
  label: string;
  [metricName: string]: string | number;
};

export const mapMetricsByCountry = (metrics: OrganizationGlobalSmsMetric[]) => {
  const byCountry = metrics.reduce(
    (acc, metric) => {
      const { recipientCountryName, metricName, value } = metric;
      return acc[recipientCountryName]
        ? {
            ...acc,
            [recipientCountryName]: {
              ...acc[recipientCountryName],
              [metricName]: value,
            },
          }
        : {
            ...acc,
            [recipientCountryName]: {
              label: recipientCountryName,
              [metricName]: value,
            },
          };
    },
    {} as Record<string, GlobalSmsDisplayMetric>,
  );
  return Object.values(byCountry);
};

export const createCsvRowKey = (metric: ProjectMetric) =>
  `${metric.project.id}-${metric.recipientCountryName}`;

export const mapGlobalSmsMetricsToRowKey = (metrics: Metric[]) =>
  metrics.reduce(
    (acc, metric) => {
      if (!isProjectGlobalSmsMetric(metric)) return acc;

      const rowKey = createCsvRowKey(metric as ProjectMetric);

      return !acc[rowKey]
        ? { ...acc, [rowKey]: [metric] }
        : { ...acc, [rowKey]: [...acc[rowKey], metric] };
    },
    {} as Record<string, Metric[]>,
  );

export const createProjectBreakdownFileName = (project: Project | undefined) => {
  const { id: projectId = '', name: projectName = '' } = project as Project;
  const hyphenatedProjectName = projectName.split(' ').join('-');
  const currentDate = new Date().toISOString().split('T')[0];
  return `Global-SMS_${projectId}-${hyphenatedProjectName}_${currentDate}.csv`;
};

const COLUMN_NAMES = {
  ORG_ID: 'Org ID',
  ORG_NAME: 'Org name',
  PROJECT_ID: 'Project ID',
  PROJECT_NAME: 'Project name',
  START_DATE: 'Selected start date',
  END_DATE: 'Selected end date',
  RECIPIENT_COUNTRY_NAME: 'Recipient country name',
  RECIPIENT_COUNTRY_CALLING_CODE: 'Recipient country calling code',
  SENDER_PHONE_NUMBER_TYPE: 'Sender phone number type',
  SMS_SEGMENTS_SENT: 'SMS segments sent',
  SMS_SEGMENTS_RECEIVED: 'SMS segments received',
  SMS_SEGMENTS_TOTAL: 'SMS segments total',
  MMS_ATTACHMENTS_SENT: 'MMS sent',
  MMS_ATTACHMENTS_RECEIVED: 'MMS received',
  MMS_ATTACHMENTS_TOTAL: 'MMS total',
  SUCCESSFUL_SMS_VERIFICATIONS: 'Successful SMS verifications',
};

export const GLOBAL_SMS_CSV_HEADERS = Object.values(COLUMN_NAMES).map((header) => ({
  label: header,
  key: header,
}));

type OrgLevelValues = {
  orgId: number;
  companyName: string;
};

type DateRangeValues = {
  startDate: string;
  endDate: string;
};

export const mapOrgLevelValuesToColumnNames = (orgLevelValues: OrgLevelValues) => ({
  [COLUMN_NAMES.ORG_ID]: orgLevelValues.orgId,
  [COLUMN_NAMES.ORG_NAME]: orgLevelValues.companyName,
});

export const mapDateRangeValuesToColumnNames = (dateRangeValues: DateRangeValues) => ({
  [COLUMN_NAMES.START_DATE]: dateRangeValues.startDate,
  [COLUMN_NAMES.END_DATE]: dateRangeValues.endDate,
});

export const mapCommonRowValuesToColumnNames = (metric: Metric) => ({
  [COLUMN_NAMES.PROJECT_ID]: metric?.project?.id || '',
  [COLUMN_NAMES.PROJECT_NAME]: metric?.project?.name || '',
  [COLUMN_NAMES.RECIPIENT_COUNTRY_NAME]: metric.recipientCountryName || '',
  [COLUMN_NAMES.RECIPIENT_COUNTRY_CALLING_CODE]: metric.recipientCountryCallingCode || '',
  [COLUMN_NAMES.SENDER_PHONE_NUMBER_TYPE]: metric.senderPhoneNumberType || '',
});

export const mapSmsMetricValuesToColumnNames = (valuesByMetricName: Record<string, number>) => ({
  [COLUMN_NAMES.SMS_SEGMENTS_SENT]: valuesByMetricName[SMS_SEGMENTS_SENT],
  [COLUMN_NAMES.SMS_SEGMENTS_RECEIVED]: valuesByMetricName[SMS_SEGMENTS_RECEIVED],
  [COLUMN_NAMES.SMS_SEGMENTS_TOTAL]: valuesByMetricName[SMS_SEGMENTS_TOTAL],
  [COLUMN_NAMES.MMS_ATTACHMENTS_SENT]: valuesByMetricName[MMS_ATTACHMENTS_SENT],
  [COLUMN_NAMES.MMS_ATTACHMENTS_RECEIVED]: valuesByMetricName[MMS_ATTACHMENTS_RECEIVED],
  [COLUMN_NAMES.MMS_ATTACHMENTS_TOTAL]: valuesByMetricName[MMS_ATTACHMENTS_TOTAL],
  [COLUMN_NAMES.SUCCESSFUL_SMS_VERIFICATIONS]: valuesByMetricName[SUCCESSFUL_SMS_VERIFICATIONS],
});

export const createGlobalSmsCsvRows = (
  metricsByRowKey: Record<string, Metric[]>,
  orgLevelValues: OrgLevelValues,
  dateRangeValues: DateRangeValues,
) =>
  Object.values(metricsByRowKey).map((metric) => {
    const valuesByMetricName = metric.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.metricName]: curr.value,
      }),
      {} as Record<string, number>,
    );
    return {
      ...mapOrgLevelValuesToColumnNames(orgLevelValues),
      ...mapDateRangeValuesToColumnNames(dateRangeValues),
      ...mapCommonRowValuesToColumnNames(metric[0]),
      ...mapSmsMetricValuesToColumnNames(valuesByMetricName),
    };
  });
